import {useEffect, useRef, useState} from 'react';
import {PrizeData} from './modal/prize';

export function useWheelContainer(options: {
  baseRotateCount: number;
  beforeSpin?: () => boolean;
  requestSpin: () => Promise<{spinEndRotate: number; prize: PrizeData} | false>;
  onShowPrize?: (prize: PrizeData) => void;
}) {
  const {baseRotateCount, requestSpin} = options;
  const [isSpinning, setIsSpinning] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [nextRotate, setNextRotate] = useState(-1);
  const prizeRef = useRef<PrizeData | null>(null);
  const spinRotateCount = useRef(0);
  const spinTimer = useRef<NodeJS.Timeout | undefined>();
  const handleInfiniteSpin = () => {
    setIsSpinning(true);
    setNextRotate(-1);
    spinRotateCount.current = baseRotateCount;
  };

  const handleSpinEnd = (finalRotate: number) => {
    setNextRotate(finalRotate);
    if (spinTimer.current) {
      clearTimeout(spinTimer.current);
      spinTimer.current = undefined;
    }
  };

  const terminateSpin = () => {
    setIsSpinning(false);
    setRotation(0);
    setNextRotate(-1);
    spinRotateCount.current = 0;
  };

  useEffect(() => {
    if (!isSpinning) {
      return;
    }
    if (nextRotate < 0) {
      spinRotateCount.current = spinRotateCount.current + 1;
      setRotation(360 * spinRotateCount.current);
      spinTimer.current = setTimeout(() => {
        setNextRotate(rotate => rotate - 1);
      }, 1000);
      return;
    }
    const targetAngle = nextRotate;
    const baseRotation = spinRotateCount.current * 360;
    const finalRotation = baseRotation + (360 - targetAngle);
    setRotation(finalRotation);
    setTimeout(() => {
      setIsSpinning(false);
      setRotation(finalRotation - baseRotation);
      if (options.onShowPrize && prizeRef.current) {
        options.onShowPrize(prizeRef.current);
      }
    }, 1000 * baseRotateCount);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nextRotate, isSpinning, baseRotateCount]);

  const handleSpin = () => {
    if (isSpinning) {
      return;
    }
    if (options.beforeSpin && !options.beforeSpin()) {
      return;
    }

    handleInfiniteSpin();
    requestSpin().then(res => {
      if (res) {
        prizeRef.current = res.prize;
        handleSpinEnd(res.spinEndRotate);
      } else {
        terminateSpin();
      }
    });
  };

  return {
    handleSpin,
    rotation,
    isSpinning,
  };
}
